import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { Agent, Cache } from 'core/models';
import { AgentsService } from './agents.service';
import { ProfileService } from './profile.service';
import { map } from 'rxjs/operators';
import { ROLES } from 'core/services/assets/constants';

@Injectable()
export class AgentsCacheService {
  public agentsCache = new Cache();
  public clientAgentsCache = new Cache();
  public clientAgentsSubject = new BehaviorSubject([]);
  public distributorAgentsSubject = new BehaviorSubject([]);
  ROLES = ROLES;

  constructor(
    public agentsService: AgentsService,
    public profileService: ProfileService,
    public route: ActivatedRoute,
    public router: Router
  ) {}

  getGlobalAgents(): Observable<any> {
    const globalRoles = this.profileService.getScopeRoles('global').map((aRole) => aRole.name);

    return from(this.agentsService.fetch({ role: globalRoles, limit: 1000, blocked: 'false', sort: 'name' })).pipe(
      map((agents: any) => {
        if (agents && agents.docs) {
          this.agentsCache.data = agents.docs.sort(this.sortAgents);
          this.updateDistributorAgents();
        }
        return this.agentsCache;
      })
    );
  }

  updateDistributorAgents(distributorId?: string) {
    if (distributorId) {
      this.agentsService
        .fetch({
          role: this.profileService.getScopeRoles('partner').map((aRole) => aRole.name),
          distributorId,
          limit: 1000,
          blocked: 'false',
          sort: 'name'
        })
        .then((agents) => {
          const partnerAgents = agents && agents.docs ? agents.docs.sort(this.sortAgents) : [];
          this.distributorAgentsSubject.next(partnerAgents);
        });
    } else {
      const distributors = this.getAgents('distributor');
      this.distributorAgentsSubject.next(distributors);
    }
  }

  private sortAgents(agent1, agent2) {
    return (agent1.name || '').localeCompare(agent2.name || '', undefined, { sensitivity: 'accent' });
  }

  getDistributorAgents(): Observable<Agent[]> {
    return this.distributorAgentsSubject.asObservable();
  }

  updateClientAgents(clientId: any) {
    if (clientId) {
      const clientRoles = this.profileService.getRoles(ROLES.HR);
      this.agentsService
        .fetch({ clientIds: [clientId], role: clientRoles, limit: 1000, sort: 'name' })
        .then((response) => {
          const agents = response && response.docs ? response.docs.sort(this.sortAgents) : [];
          this.clientAgentsCache.data = agents;
          this.clientAgentsSubject.next(agents);
        });
    } else {
      this.clientAgentsCache.data = [];
      this.clientAgentsSubject.next([]);
    }
  }

  getClientAgents(): Observable<Agent[]> {
    return this.clientAgentsSubject.asObservable();
  }

  getAgent(id): Agent {
    return (this.agentsCache.getById(id) as Agent) || this.clientAgentsCache.getById(id);
  }

  getAgents(role?: string): Agent[] {
    const searchedRoles = this.profileService.getRoles(role);
    return this.agentsCache.data.filter((agent) => searchedRoles.includes(agent.role));
  }
}
